import { useEffect, useRef, useState, useCallback } from 'react';

// Guards browser close/refresh, header/sidebar links, menu items and browser Back while `dirty`.
export function useUnsavedChangesGuard(dirty, navigate) {
  const dirtyRef = useRef(dirty);
  const trapRef = useRef(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    dirtyRef.current = dirty;
    if (dirty && !trapRef.current) {
      window.history.pushState(window.history.state, '', window.location.href);
      trapRef.current = true;
    }
  }, [dirty]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    const onClick = (e) => {
      if (!dirtyRef.current) return;
      const el = e.target.closest('a[href], [role="menuitem"]');
      if (!el || el.closest('[data-allow-nav]')) return;
      if (el.tagName === 'A') {
        const url = new URL(el.href, window.location.href);
        if (el.target === '_blank' || url.href === window.location.href) return;
        e.preventDefault();
        e.stopPropagation();
        const internal = url.origin === window.location.origin;
        setPending(() => () => (internal ? navigate(url.pathname + url.search + url.hash) : (window.location.href = url.href)));
      } else if (!window.confirm('You have unsaved changes. Leave without saving?')) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        dirtyRef.current = false;
      }
    };
    const onPopState = () => {
      if (!trapRef.current) return;
      if (!dirtyRef.current) {
        trapRef.current = false;
        window.history.back();
        return;
      }
      window.history.pushState(window.history.state, '', window.location.href);
      setPending(() => () => { trapRef.current = false; window.history.go(-2); });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onPopState);
    };
  }, [navigate]);

  const guard = useCallback((action) => {
    if (dirtyRef.current) setPending(() => action);
    else action();
  }, []);

  const confirmLeave = useCallback(() => {
    const action = pending;
    dirtyRef.current = false;
    setPending(null);
    if (action) action();
  }, [pending]);

  const disable = useCallback(() => { dirtyRef.current = false; }, []);

  return { pending: !!pending, guard, confirmLeave, cancelLeave: () => setPending(null), disable };
}
